import { normalizeMessageOrder } from 'dashboard/helper/messageOrder';
import { mergePersonalRead } from 'dashboard/helper/personalRead';
import { mergeActivity } from 'dashboard/helper/messageActivity';
import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  listRequestId: 0,
  listError: false,
  listResultIds: [],
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

// Invalidating on reset also protects A -> B -> A navigation from old responses.
const invalidateListRequest = state => {
  state.listRequestId = (state.listRequestId || 0) + 1;
  state.listLoadingStatus = false;
  state.listError = false;
};

const includeLiveResult = (state, id) => {
  const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE || {};
  const type = state.conversationFilters?.conversationType;
  if (type && [MENTION, PARTICIPATING].includes(type)) return;
  if (!state.listResultIds.includes(id)) state.listResultIds.push(id);
};

// mutations
export const mutations = {
  BEGIN_LIST_REQUEST(state) {
    invalidateListRequest(state);
    state.listLoadingStatus = true;
  },
  INVALIDATE_LIST_REQUEST: invalidateListRequest,
  SET_LIST_ERROR(state, value) {
    state.listError = value;
  },
  SET_LIST_RESULT_IDS(state, { ids, page }) {
    state.listResultIds = [...new Set([...(page === 1 ? [] : state.listResultIds), ...ids])];
  },
  RESET_LIST_RESULTS(state) {
    state.listResultIds = [];
  },
  PRUNE_LIST_ENTITIES(state, cache) {
    const retained = new Set([...cache.activeIds, ...Object.values(cache.queries).flatMap(query => query.orderedIds)]);
    state.allConversations = state.allConversations.filter(chat =>
      retained.has(chat.id) || chat.id === state.selectedChatId || chat.dataFetched ||
      chat.messages?.length || state.syncConversationsMessages[chat.id] || state.attachments[chat.id]);
  },
  REMOVE_UNSUBMITTED_MESSAGE(_state, { conversationId, id }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;
    // Strict ID equality cannot remove a later server row matched by echo_id.
    chat.messages = (chat.messages || []).filter(message => message.id !== id);
  },
  SET_WHATSAPP_MESSAGE_LOAD(_state, { id, loading, error }) {
    const chat = getConversationById(_state)(id);
    if (!chat) return;
    if (typeof loading === 'boolean') chat.bcw_messages_loading = loading;
    if (typeof error === 'boolean') chat.bcw_messages_error = error;
  },
  SET_INITIAL_MESSAGE_LOAD(_state, { id, loading, error }) {
    const chat = getConversationById(_state)(id);
    if (!chat) return;
    if (typeof loading === 'boolean') chat.initial_messages_loading = loading;
    if (typeof error === 'boolean') chat.initial_messages_error = error;
  },
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      const existing = newAllConversations[indexInCurrentList];
      const summary = conversation.list_summary === true;
      conversation = mergePersonalRead(conversation, existing);
      if (indexInCurrentList >= 0) mergeActivity(conversation, newAllConversations[indexInCurrentList]);
      if (summary) {
        // Keep previews separate from complete message history for every chat,
        // including chats which are not currently selected.
        const { messages: preview, ...fields } = conversation;
        const stale = existing && Number(conversation.updated_at) < Number(existing.updated_at);
        const next = stale ? { ...existing } : { messages: [], ...existing, ...fields,
          list_preview_messages: preview || existing?.list_preview_messages || [] };
        // Read clocks are independent from conversation.updated_at.
        if (stale) Object.keys(fields).filter(key => key.startsWith('personal_') ||
          ['unread_count', 'agent_last_seen_at', 'assignee_last_seen_at'].includes(key)).forEach(key => { next[key] = fields[key]; });
        if (indexInCurrentList < 0) newAllConversations.push(next);
        else newAllConversations[indexInCurrentList] = next;
      } else if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (conversation.id !== _state.selectedChatId && !existing.dataFetched && !existing.messages?.length) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...existingConversation,
          ...conversation,
          list_preview_messages: conversation.messages,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
        };
      }
    });
    newAllConversations.forEach(normalizeMessageOrder);
    _state.allConversations = newAllConversations;
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    invalidateListRequest(_state);
    _state.allConversations = [];
    _state.listResultIds = [];
    _state.attachments = {};
    _state.selectedChatId = null;
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      const [chat] = _state.allConversations.filter(c => c.id === id);
      // A live arrival can overlap the initial HTTP page. Preserve its newer
      // state and any pending reply instead of inserting the same ID twice.
      const existingIds = new Set(chat.messages.map(message => message.id));
      chat.messages.unshift(...data.filter(message => !existingIds.has(message.id)));
      normalizeMessageOrder(chat);
    }
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    chat.messages = data;
    normalizeMessageOrder(chat);
  },

  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee, assigneeType }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.meta.assignee = assignee;
      chat.meta.assignee_type = assigneeType;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.last_activity_at = lastActivityAt;
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes }
  ) {
    const conversation = _state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    mergeActivity(chat, message.conversation);
    const existingIndex = chat.messages.findIndex(item => item.id === message.id);
    if (chat.message_activity_enabled && existingIndex !== -1) {
      chat.messages[existingIndex] = message;
      normalizeMessageOrder(chat);
      return;
    }
    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      if (!chat.personal_read_enabled && !message.conversation?.personal_read_enabled) chat.unread_count = unreadCount;
      if (message.conversation?.personal_read_enabled) chat.personal_read_enabled = true;
      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
    normalizeMessageOrder(chat);
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    normalizeMessageOrder(conversation);
    const exists = _state.allConversations.some(c => c.id === conversation.id);
    if (!exists) {
      _state.allConversations.push(conversation);
    }
    includeLiveResult(_state, conversation.id);
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.listResultIds = _state.listResultIds.filter(id => id !== conversationId);
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }
      includeLiveResult(_state, conversation.id);

      const { messages, ...updates } = mergePersonalRead(conversation, selectedConversation);
      const nextConversation = { ...selectedConversation, ...updates };
      mergeActivity(nextConversation, selectedConversation);
      allConversations[index] = nextConversation;
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      if (![MENTION, PARTICIPATING].includes(conversationType)) {
        _state.allConversations.push(conversation);
        includeLiveResult(_state, conversation.id);
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat && !chat.personal_read_enabled) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  APPLY_PERSONAL_READ(_state, projection) {
    const chat = getConversationById(_state)(Number(projection.id));
    if (chat) Object.assign(chat, mergePersonalRead(projection, chat));
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](
    _state,
    { conversationId, callStatus, callSid }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const message = (chat.messages || []).find(
      m =>
        m.content_type === CONTENT_TYPES.VOICE_CALL &&
        m.call?.provider_call_id === callSid
    );
    if (!message?.call) return;

    message.call = { ...message.call, status: callStatus };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
